import type { LinkWorkSupportedType } from '@/lib/utils/link-work-module-types'
import type { KeyboardEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { SmartTimeInput } from '@/components/shared/SmartTimeInput'
import { LinkWorkSelector } from '@/components/time-entry/LinkWorkSelector'
import { SplitTimeEntryDialog } from '@/components/time-entry/SplitTimeEntryDialog'
import { TimeEntryTagging } from '@/components/time-entry/TimeEntryTagging'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DateInputPicker } from '@/components/ui/date-input-picker'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  useCreateTimeEntryMutation,
  useDeleteTimeEntryMutation,
  useUpdateTimeEntryMutation,
} from '@/hooks/mutations/useTimeEntryMutations'
import { useOrganizationSettingsQuery } from '@/hooks/queries/useOrganizationSettingsQuery'
import {
  useMyPeriodStatusQuery,
  usePayPeriodsQuery,
} from '@/hooks/queries/usePayPeriodsQuery'
import { useTimeEntryQuery } from '@/hooks/queries/useTimeEntriesQuery'
import { useSubscriptionFeatures } from '@/hooks/useSubscriptionFeatures'
import { useReplaceTagsForTimeEntry } from '@/hooks/useTimeEntryTags'
import { useTimeFormat } from '@/hooks/useTimeFormat'
import { useTimeTimezone } from '@/hooks/useTimeTimezone'
import { ModuleSearchResult } from '@/lib/api/search'
import { cn } from '@/lib/utils'
import {
  getLinkWorkAllowedModuleTypes,
  LINK_WORK_SUPPORTED_TYPES,
} from '@/lib/utils/link-work-module-types'
import {
  calculateHoursFromTime,
  formatDuration,
  formatTimeString,
  getMaxHours,
  safeParseDate,
  syncHoursToEndTime,
  syncTimeToHours,
  validateTimeRange,
} from '@/lib/utils/time-validation'
import RoleSelector from '@/pages/team/_components/RoleSelector'
import { useOrganizationStore } from '@/store/organization.store'
import {
  createDateOnlyUTC,
  getDateOnlyForDisplay,
  toDateOnlyString,
} from '@/utils/date'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  CalendarIcon,
  CircleNotchIcon,
  ClockIcon,
  GlobeIcon,
  PlusIcon,
  XIcon,
} from '@phosphor-icons/react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import * as z from 'zod'

import type {
  CreateTimeEntry,
  TimeEntry,
  TimeEntryLinkedModule,
} from '@scrambl/shared'
import {
  BillingConfigurationEnforcementModeEnum,
  ResourceModuleSettingsSchema,
  TimeEntryModuleSettingsSchema,
  TimeEntryStatusEnum,
  TimeEntryTypeEnum,
  UserPayPeriodStatusEnum,
} from '@scrambl/shared'
import { getModifierKeyLabel } from '@/utils/keyboard-utils'

const TIME_ENTRY_MODULE_LINK_TYPES = [
  ...LINK_WORK_SUPPORTED_TYPES,
  'RESOURCE',
  'TEAM',
] as const
const TIME_ENTRY_MODULE_LINK_TYPE_SET = new Set<string>(
  TIME_ENTRY_MODULE_LINK_TYPES,
)

const timeEntrySchema = z.object({
  date: z.date({
    required_error: 'Date is required',
  }),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  hours: z.string().optional(),
  moduleType: z.enum(TIME_ENTRY_MODULE_LINK_TYPES).optional(),
  moduleId: z.string().optional(),
  roleId: z.string().uuid().optional().nullable(),
  description: z.string().optional(),
  billable: z.boolean().default(true),
  status: z.string().default('DRAFT'),
})
type TimeEntryFormData = z.infer<typeof timeEntrySchema>
type TimeEntryModuleLinkType =
  | TimeEntryFormData['moduleType']
  | LinkWorkSupportedType
const DEFAULT_BILL_TO_TYPES = [...LINK_WORK_SUPPORTED_TYPES, 'SUBTASK']

interface CreateTimeEntryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  entry?: TimeEntry | null
  defaultDate?: Date
  defaultEndTime?: Date
  defaultModuleType?: TimeEntryModuleLinkType
  defaultModuleId?: string
  defaultModuleName?: string
  defaultRoleId?: string
  targetUserId?: string
  onSaved?: (entry: TimeEntry) => void
  disableNestedSplitDialog?: boolean
}

function getDefaultStartTime(
  entry?: TimeEntry | null,
  defaultDate?: Date,
  defaultEndTime?: Date,
) {
  if (entry?.startTime) {
    return formatTimeString(safeParseDate(entry.startTime))
  }
  // Return start time if defaultDate has time info (hours/minutes set)
  if (
    defaultDate &&
    defaultDate.getHours &&
    (defaultDate.getHours() !== 0 || defaultDate.getMinutes() !== 0)
  ) {
    return formatTimeString(defaultDate)
  }
  // Also handle when both defaultDate and defaultEndTime are provided
  if (defaultDate && defaultEndTime && defaultDate.getHours) {
    return formatTimeString(defaultDate)
  }
  return ''
}

function getDefaultEndTime(entry?: TimeEntry | null, defaultEndTime?: Date) {
  if (entry?.endTime) {
    return formatTimeString(safeParseDate(entry.endTime))
  }
  if (defaultEndTime && defaultEndTime.getHours) {
    return formatTimeString(defaultEndTime)
  }
  return ''
}

function getDefaultHoursValue(
  entry?: TimeEntry | null,
  defaultDate?: Date,
  defaultEndTime?: Date,
  formatter?: (hours: number) => string,
) {
  const fmt = formatter || ((h: number) => formatDuration(h))
  if (entry?.duration) {
    return fmt(Number(entry.duration))
  }
  // Calculate hours from start and end time if both are provided
  if (defaultDate && defaultEndTime) {
    const startStr = formatTimeString(defaultDate)
    const endStr = formatTimeString(defaultEndTime)
    const calculatedHours = calculateHoursFromTime(startStr, endStr)
    if (calculatedHours > 0) {
      return fmt(calculatedHours)
    }
  }
  return ''
}

function getEntryDateForPayload(date: Date) {
  return createDateOnlyUTC(toDateOnlyString(date))
}

function createTimeOnEntryDate(date: Date, time: string) {
  const [year, month, day] = toDateOnlyString(date).split('-').map(Number)
  const [hour, minute] = time.split(':').map(Number)
  return new Date(year, month - 1, day, hour, minute, 0, 0)
}

export function CreateTimeEntryDialog({
  open,
  onOpenChange,
  entry,
  defaultDate,
  defaultEndTime,
  defaultModuleType,
  defaultModuleId,
  defaultModuleName,
  defaultRoleId,
  targetUserId,
  onSaved,
  disableNestedSplitDialog = false,
}: CreateTimeEntryDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [availableBillingRoleCount, setAvailableBillingRoleCount] = useState<
    number | null
  >(null)
  const [isSplitDialogOpen, setIsSplitDialogOpen] = useState(false)
  // Extra "Link to" rows beyond the primary one (create mode only). A null
  // slot is an empty row waiting for a selection.
  const [extraLinkedModules, setExtraLinkedModules] = useState<
    Array<TimeEntryLinkedModule | null>
  >([])
  // Draft split flow: the validated payload is held here while the split
  // dialog is open; the entry is only created when the user confirms the split.
  // Everything is snapshotted (entry + modules) so the split dialog's props
  // keep a stable identity and it doesn't re-init the user's split edits.
  const [draftSplitData, setDraftSplitData] = useState<{
    payload: Partial<CreateTimeEntry>
    totalMinutes: number
    draftEntry: TimeEntry
    linkedModules: TimeEntryLinkedModule[]
  } | null>(null)
  const [isDraftSplitOpen, setIsDraftSplitOpen] = useState(false)
  const [selectedModuleName, setSelectedModuleName] = useState<
    string | undefined
  >(undefined)
  const [selectedModuleProjectId, setSelectedModuleProjectId] = useState<
    string | null
  >(null)
  const [selectedModuleEntityId, setSelectedModuleEntityId] = useState<
    string | null
  >(null)
  const [pendingTaggedUserIds, setPendingTaggedUserIds] = useState<string[]>([])
  const { checkFeature } = useSubscriptionFeatures()
  const { getCurrentUserMembership } = useOrganizationStore()
  const currentMembership = getCurrentUserMembership()
  const { activeTimezoneAbbr, userTimezoneLabel, timezonesMatch } =
    useTimeTimezone()
  const { data: settingsData } = useOrganizationSettingsQuery()
  const {
    formatTime,
    parseTime: parseTimeInput,
    getTimeFormat,
  } = useTimeFormat()

  // Helper to convert display-format hours string to decimal for calculations
  const toDecimalStr = (displayStr: string): string => {
    if (!displayStr) return ''
    const parsed = parseTimeInput(displayStr)
    return isNaN(parsed) || parsed === 0 ? displayStr : formatDuration(parsed)
  }

  // Helper to convert decimal hours to display-format string
  const toDisplayStr = (decimalStr: string): string => {
    if (!decimalStr) return ''
    const num = parseFloat(decimalStr)
    return isNaN(num) ? decimalStr : formatTime(num)
  }

  const { data: entryDetails } = useTimeEntryQuery(entry?.id, {
    enabled: open && !!entry?.id,
  })
  const resolvedEntry = entryDetails ?? entry ?? null
  // Submitted/approved entries are awaiting or past review and cannot be edited.
  const isEntryReadOnly =
    resolvedEntry?.status === TimeEntryStatusEnum.SUBMITTED ||
    resolvedEntry?.status === TimeEntryStatusEnum.APPROVED
  const linkedModules = resolvedEntry?.linkedModules ?? []
  const usesRealtimeLinkedItems = Boolean(resolvedEntry?.timerSessionId)

  const timeSettings = TimeEntryModuleSettingsSchema.parse(
    settingsData?.effectiveSettings?.modules?.TIME_ENTRY || {},
  )
  const teamSettings = ResourceModuleSettingsSchema.parse(
    settingsData?.effectiveSettings?.modules?.TEAM || {},
  )
  const billingConfiguration = teamSettings.billingConfiguration
  const linkWorkAllowedModuleTypes = getLinkWorkAllowedModuleTypes(
    timeSettings.allowedBillToTypes &&
      timeSettings.allowedBillToTypes.length > 0
      ? timeSettings.allowedBillToTypes
      : DEFAULT_BILL_TO_TYPES,
  )
  const allowManualEntry = timeSettings.allowManualEntry
  const allowTimerEntry = timeSettings.allowTimerEntry

  const createTimeEntryMutation = useCreateTimeEntryMutation()
  const deleteTimeEntryMutation = useDeleteTimeEntryMutation()
  const updateTimeEntryMutation = useUpdateTimeEntryMutation()
  // The base entry created by the draft split's beforeSplit. Kept so a failed
  // split can be retried without creating a duplicate, and rolled back if the
  // user cancels after the entry was already created.
  const createdDraftEntryRef = useRef<TimeEntry | null>(null)
  const replaceTagsForTimeEntryMutation = useReplaceTagsForTimeEntry()

  const form = useForm<TimeEntryFormData>({
    resolver: zodResolver(timeEntrySchema),
    defaultValues: {
      date: entry?.entryDate
        ? safeParseDate(entry.entryDate)
        : entry?.startTime
          ? safeParseDate(entry.startTime)
          : safeParseDate(defaultDate),
      startTime: getDefaultStartTime(entry, defaultDate, defaultEndTime),
      endTime: getDefaultEndTime(entry, defaultEndTime),
      hours: getDefaultHoursValue(
        entry,
        defaultDate,
        defaultEndTime,
        formatTime,
      ),
      moduleType: (entry?.moduleType ||
        defaultModuleType) as TimeEntryModuleLinkType,
      moduleId: entry?.moduleId || defaultModuleId || '',
      description: entry?.description || '',
      billable: entry?.billable ?? timeSettings.defaultBillable,
      status:
        entry?.status ||
        (timeSettings.requireApproval
          ? timeSettings.defaultStatus
          : 'APPROVED'),
    },
  })

  const resetKey = [
    open ? 'open' : 'closed',
    entry?.id ?? 'new',
    defaultDate?.getTime() ?? '',
    defaultEndTime?.getTime() ?? '',
    defaultModuleType ?? '',
    defaultModuleId ?? '',
    defaultModuleName ?? '',
    defaultRoleId ?? '',
    timeSettings.defaultBillable,
    timeSettings.defaultStatus,
    timeSettings.requireApproval,
  ].join('|')
  const lastResetKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!open) {
      lastResetKeyRef.current = null
      return
    }

    if (lastResetKeyRef.current === resetKey) {
      return
    }

    lastResetKeyRef.current = resetKey

    form.reset({
      date: entry?.entryDate
        ? safeParseDate(entry.entryDate)
        : entry?.startTime
          ? safeParseDate(entry.startTime)
          : safeParseDate(defaultDate),
      startTime: getDefaultStartTime(entry, defaultDate, defaultEndTime),
      endTime: getDefaultEndTime(entry, defaultEndTime),
      hours: getDefaultHoursValue(
        entry,
        defaultDate,
        defaultEndTime,
        formatTime,
      ),
      moduleType: (entry?.moduleType ||
        defaultModuleType) as TimeEntryModuleLinkType,
      moduleId: entry?.moduleId || defaultModuleId || '',
      roleId: entry?.roleId || defaultRoleId || undefined,
      description: entry?.description || '',
      billable: entry?.billable ?? timeSettings.defaultBillable,
      status:
        entry?.status ||
        (timeSettings.requireApproval
          ? timeSettings.defaultStatus
          : 'APPROVED'),
    })
    setSelectedModuleName(entry?.moduleName ?? defaultModuleName ?? undefined)
    setSelectedModuleProjectId(null)
    setSelectedModuleEntityId(null)
    setAvailableBillingRoleCount(null)
    setPendingTaggedUserIds([])
    setExtraLinkedModules([])
    setDraftSplitData(null)
    setIsDraftSplitOpen(false)
    createdDraftEntryRef.current = null
  }, [
    defaultDate,
    defaultEndTime,
    defaultModuleId,
    defaultModuleType,
    defaultModuleName,
    defaultRoleId,
    entry,
    form,
    formatTime,
    open,
    resetKey,
    timeSettings.defaultBillable,
    timeSettings.defaultStatus,
    timeSettings.requireApproval,
  ])

  // Check if selected date falls in a submitted/approved/closed pay period
  const watchedDate = form.watch('date')
  const { data: allPeriodsData } = usePayPeriodsQuery({
    limit: 200,
    enabled: open,
  })
  const matchingPeriod = (() => {
    if (!watchedDate || !allPeriodsData?.data) return null
    const selected = new Date(watchedDate)
    selected.setHours(12, 0, 0, 0)
    return (
      allPeriodsData.data.find(period => {
        const start = getDateOnlyForDisplay(period.startDate)
        const end = getDateOnlyForDisplay(period.endDate)
        if (!start || !end) return false
        start.setHours(0, 0, 0, 0)
        end.setHours(23, 59, 59, 999)
        return selected >= start && selected <= end
      }) ?? null
    )
  })()
  const { data: matchingUserPeriod } = useMyPeriodStatusQuery(
    matchingPeriod?.id,
    {
      enabled: open && !!matchingPeriod?.id && !targetUserId,
    },
  )
  const periodWarning = (() => {
    if (targetUserId || !matchingUserPeriod) return null
    if (matchingUserPeriod.status === UserPayPeriodStatusEnum.SUBMITTED) {
      // Adding a brand-new entry to a submitted period is only blocked when the
      // org enables "block new entries after submit". Editing/moving an existing
      // entry into a submitted period is always blocked.
      if (!entry && !timeSettings.blockNewEntriesAfterSubmit) {
        return null
      }
      return 'Your timesheet for this period has been submitted. Recall your submission to make changes.'
    }
    if (matchingUserPeriod.status === UserPayPeriodStatusEnum.APPROVED) {
      return 'Your timesheet for this period has been approved. Contact your admin to reopen it.'
    }
    if (matchingUserPeriod.status === UserPayPeriodStatusEnum.CLOSED) {
      return 'Your timesheet for this period has been closed. No changes can be made.'
    }
    return null
  })()

  const startTime = form.watch('startTime')
  const timeEntryHint =
    allowManualEntry && allowTimerEntry
      ? 'Enter start/end times or total hours. Fields sync automatically.'
      : allowManualEntry
        ? 'Enter total hours for this entry.'
        : 'Enter start and end times for this entry.'

  const syncTimeFields = (
    overrides: Partial<Pick<TimeEntryFormData, 'startTime' | 'endTime'>> = {},
  ) => {
    // Get latest values from form (not stale watch values)
    const currentStartTime =
      overrides.startTime ?? form.getValues('startTime')
    const currentEndTime = overrides.endTime ?? form.getValues('endTime')
    const currentHours = form.getValues('hours')

    if (currentStartTime && currentEndTime) {
      // Both start and end are filled - calculate hours
      const calculatedHours = syncTimeToHours(currentStartTime, currentEndTime)
      if (calculatedHours) {
        form.setValue('hours', toDisplayStr(calculatedHours), {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        })
        form.clearErrors('startTime')
        form.clearErrors('endTime')
      } else {
        form.setError('endTime', {
          type: 'manual',
          message: 'End time must be after start time',
        })
      }
    } else if (currentStartTime && currentHours && !currentEndTime) {
      // Start time entered with hours already filled but no end time - calculate end time
      const decimalHours = toDecimalStr(currentHours)
      const result = syncHoursToEndTime(currentStartTime, decimalHours)

      if (result.hours !== decimalHours) {
        form.setValue('hours', toDisplayStr(result.hours), {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        })
        form.setError('hours', {
          type: 'manual',
          message: `Maximum ${formatTime(getMaxHours(currentStartTime))} hours from this start time`,
        })
      } else {
        form.clearErrors('hours')
      }

      if (result.endTime) {
        form.setValue('endTime', result.endTime, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        })
        form.clearErrors('endTime')
      }
    }
  }

  const handleTimeBlur = () => {
    syncTimeFields()
  }

  const handleTimeChange = (
    name: 'startTime' | 'endTime',
    value: string,
  ) => {
    form.setValue(name, value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
    syncTimeFields({ [name]: value })
  }

  const handleHoursBlur = () => {
    // Get latest values from form (not stale watch values)
    const currentStartTime = form.getValues('startTime')
    const currentHours = form.getValues('hours')

    // Re-format the display value on blur (e.g., normalize "2.5" → "2:30" for HH:MM)
    if (currentHours) {
      const parsed = parseTimeInput(currentHours)
      if (!isNaN(parsed) && parsed > 0) {
        form.setValue('hours', formatTime(parsed))
      }
    }

    if (currentStartTime && currentHours) {
      const decimalHours = toDecimalStr(currentHours)
      const result = syncHoursToEndTime(currentStartTime, decimalHours)

      if (result.hours !== decimalHours) {
        form.setValue('hours', toDisplayStr(result.hours))
        form.setError('hours', {
          type: 'manual',
          message: `Maximum ${formatTime(getMaxHours(currentStartTime))} hours from this start time`,
        })
      } else {
        form.clearErrors('hours')
      }

      if (result.endTime) {
        form.setValue('endTime', result.endTime)
        form.clearErrors('endTime')
      }
    }
  }

  // Shared by Create/Update and the multi-link "Split Times" flow: runs every
  // guard and returns the entry payload, or null (after toasting) when invalid.
  const validateAndBuildPayload = (
    data: TimeEntryFormData,
  ): Partial<CreateTimeEntry> | null => {
    if (timeSettings.requireDescription && !data.description?.trim()) {
      toast.error('Description is required for time entries')
      return null
    }

    if (
      timeSettings.requireModuleLink &&
      (!data.moduleId || !data.moduleType)
    ) {
      toast.error('Please link this time entry to an enabled record')
      return null
    }

    if (
      data.billable &&
      !data.roleId &&
      availableBillingRoleCount !== null &&
      availableBillingRoleCount > 0 &&
      billingConfiguration.enforceBillingConfiguration &&
      billingConfiguration.enforcementMode ===
        BillingConfigurationEnforcementModeEnum.HARD_BLOCK
    ) {
      toast.error('Select a billing role before saving billable time')
      return null
    }

    if (!timeSettings.allowFutureEntries) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const entryDate = new Date(data.date)
      entryDate.setHours(0, 0, 0, 0)
      if (entryDate.getTime() > today.getTime()) {
        toast.error('Future-dated time entries are not allowed')
        return null
      }
    }

    const parsedHoursNum = data.hours ? parseTimeInput(data.hours) : 0
    const hasHours = parsedHoursNum > 0
    const hasTimeRange = data.startTime && data.endTime

    if (!hasHours && !hasTimeRange) {
      toast.error('Please enter either hours or both start and end times')
      return null
    }

    if (hasTimeRange && !allowTimerEntry) {
      toast.error('Timer-based time entries are disabled')
      return null
    }

    if (!hasTimeRange && hasHours && !allowManualEntry) {
      toast.error('Manual time entries are disabled')
      return null
    }

    if (hasTimeRange) {
      const validation = validateTimeRange(data.startTime!, data.endTime!)
      if (!validation.isValid) {
        toast.error(validation.error || 'Invalid time range')
        return null
      }

      const calculatedHours = calculateHoursFromTime(
        data.startTime!,
        data.endTime!,
      )
      if (calculatedHours <= 0) {
        toast.error('End time must be after start time')
        return null
      }
    }

    if (hasHours && data.startTime) {
      const maxHours = getMaxHours(data.startTime)
      if (parsedHoursNum > maxHours) {
        toast.error(
          `Hours cannot exceed ${formatTime(maxHours)} from this start time`,
        )
        return null
      }
    }

    const roundDuration = (durationHours: number) => {
      if (timeSettings.roundingInterval <= 0) return durationHours
      const roundedMinutes =
        Math.round((durationHours * 60) / timeSettings.roundingInterval) *
        timeSettings.roundingInterval
      return Math.round((roundedMinutes / 60) * 100) / 100
    }

    const moduleLinkPayload =
      data.moduleType && data.moduleId
        ? {
            moduleType: data.moduleType,
            moduleId: data.moduleId,
            ...(selectedModuleName ? { moduleName: selectedModuleName } : {}),
          }
        : entry
          ? { moduleType: null, moduleId: null, moduleName: null }
          : {}
    const rolePayload = data.roleId ? { roleId: data.roleId } : {}
    const entryDate = getEntryDateForPayload(data.date)

    if (hasTimeRange) {
      const startTime = createTimeOnEntryDate(data.date, data.startTime!)
      const endTimeDate = createTimeOnEntryDate(data.date, data.endTime!)

      const duration = roundDuration(
        calculateHoursFromTime(data.startTime!, data.endTime!),
      )

      if (
        timeSettings.minimumDuration > 0 &&
        duration * 60 < timeSettings.minimumDuration
      ) {
        toast.error(
          `Time entries must be at least ${timeSettings.minimumDuration} minutes`,
        )
        return null
      }

      if (
        timeSettings.maxHoursPerEntry > 0 &&
        duration > timeSettings.maxHoursPerEntry
      ) {
        toast.error(
          `Time entries cannot exceed ${timeSettings.maxHoursPerEntry} hours`,
        )
        return null
      }

      return {
        entryType: TimeEntryTypeEnum.TIMER,
        entryDate,
        startTime: startTime,
        endTime: endTimeDate,
        duration: duration,
        description: data.description,
        billable: data.billable,
        ...moduleLinkPayload,
        ...rolePayload,
      }
    }

    const roundedHours = roundDuration(parsedHoursNum)

    if (
      timeSettings.minimumDuration > 0 &&
      roundedHours * 60 < timeSettings.minimumDuration
    ) {
      toast.error(
        `Time entries must be at least ${timeSettings.minimumDuration} minutes`,
      )
      return null
    }

    if (
      timeSettings.maxHoursPerEntry > 0 &&
      roundedHours > timeSettings.maxHoursPerEntry
    ) {
      toast.error(
        `Time entries cannot exceed ${timeSettings.maxHoursPerEntry} hours`,
      )
      return null
    }

    return {
      entryType: TimeEntryTypeEnum.MANUAL,
      entryDate,
      duration: roundedHours,
      description: data.description,
      billable: data.billable,
      ...moduleLinkPayload,
      ...rolePayload,
    }
  }

  const onSubmit = async (data: TimeEntryFormData) => {
    if (isEntryReadOnly) {
      toast.error(
        resolvedEntry?.status === TimeEntryStatusEnum.APPROVED
          ? 'This time entry has been approved and can no longer be edited.'
          : 'This time entry has been submitted and can no longer be edited. Recall it first to make changes.',
      )
      return
    }

    if (!checkFeature('time_tracking')) {
      toast.error(
        'Time tracking is not available in your current subscription plan',
      )
      return
    }

    setIsSubmitting(true)
    try {
      const payload = validateAndBuildPayload(data)
      if (!payload) {
        return
      }

      const linkedModulesPayload: TimeEntryLinkedModule[] =
        data.moduleType && data.moduleId
          ? [
              {
                moduleType:
                  data.moduleType as TimeEntryLinkedModule['moduleType'],
                moduleId: data.moduleId,
                ...(selectedModuleName
                  ? { moduleName: selectedModuleName }
                  : {}),
              },
            ]
          : []

      let savedEntry: TimeEntry

      if (entry) {
        const updatePayload = {
          ...payload,
          ...(usesRealtimeLinkedItems
            ? { linkedModules: [] }
            : { linkedModules: linkedModulesPayload }),
        }
        savedEntry = await updateTimeEntryMutation.mutateAsync({
          id: entry.id,
          data: updatePayload,
        })
      } else {
        const createPayload = targetUserId
          ? { ...payload, userId: targetUserId }
          : payload
        savedEntry = await createTimeEntryMutation.mutateAsync({
          data: createPayload as CreateTimeEntry,
        })
      }

      if (!entry) {
        const uniqueTaggedUserIds = Array.from(new Set(pendingTaggedUserIds))
        if (uniqueTaggedUserIds.length > 0) {
          try {
            await replaceTagsForTimeEntryMutation.mutateAsync({
              timeEntryId: savedEntry.id,
              data: { taggedUserIds: uniqueTaggedUserIds },
              suppressErrorToast: true,
            })
          } catch {
            // The entry itself saved; only the tag sync failed (after retries).
            // Surface it explicitly so the members aren't silently dropped.
            const failedCount = uniqueTaggedUserIds.length
            toast.error(
              `Time entry saved, but couldn't tag ${failedCount} member${failedCount === 1 ? '' : 's'}. Reopen the entry to add them.`,
            )
          }
        }
      }

      onOpenChange(false)
      form.reset()
      setPendingTaggedUserIds([])
      onSaved?.(savedEntry)
    } catch (error) {
      // Toast is handled by mutation hooks
    } finally {
      setIsSubmitting(false)
    }
  }

  const onInvalid = () => {
    toast.error('Please fix the highlighted fields before saving')
  }

  // Multi-link create: extra "Link to" rows split the entry's time across the
  // linked items instead of creating one linked entry.
  const canMultiLink = !entry && !disableNestedSplitDialog && !isEntryReadOnly
  const watchedModuleType = form.watch('moduleType')
  const watchedModuleId = form.watch('moduleId')
  const draftLinkedModules: TimeEntryLinkedModule[] = [
    ...(watchedModuleType && watchedModuleId
      ? [
          {
            moduleType: watchedModuleType as TimeEntryLinkedModule['moduleType'],
            moduleId: watchedModuleId,
            moduleName: selectedModuleName ?? null,
          },
        ]
      : []),
    ...extraLinkedModules.filter(
      (module): module is TimeEntryLinkedModule => module !== null,
    ),
  ]
  const isMultiLinkCreate = canMultiLink && draftLinkedModules.length > 1

  const handleExtraLinkSelect = (
    index: number,
    result: ModuleSearchResult | null,
  ) => {
    if (!result) {
      setExtraLinkedModules(prev =>
        prev.map((module, idx) => (idx === index ? null : module)),
      )
      return
    }

    if (!TIME_ENTRY_MODULE_LINK_TYPE_SET.has(result.type)) {
      return
    }

    const nextModule: TimeEntryLinkedModule = {
      moduleType: result.type as TimeEntryLinkedModule['moduleType'],
      moduleId: result.id,
      moduleName: result.name ?? null,
    }
    const isDuplicate =
      (form.getValues('moduleType') === result.type &&
        form.getValues('moduleId') === result.id) ||
      extraLinkedModules.some(
        (module, idx) =>
          idx !== index &&
          module?.moduleType === nextModule.moduleType &&
          module?.moduleId === nextModule.moduleId,
      )
    if (isDuplicate) {
      toast.error('That item is already linked.')
      return
    }

    setExtraLinkedModules(prev =>
      prev.map((module, idx) => (idx === index ? nextModule : module)),
    )
  }

  const handleRemoveExtraLink = (index: number) => {
    setExtraLinkedModules(prev => prev.filter((_, idx) => idx !== index))
  }

  // "Split Times": validate the form like Create would, but hold the payload
  // and hand off to the split dialog. Nothing is persisted until the user
  // confirms the split; Cancel returns to this form with everything intact.
  const handleSplitTimes = (data: TimeEntryFormData) => {
    if (!checkFeature('time_tracking')) {
      toast.error(
        'Time tracking is not available in your current subscription plan',
      )
      return
    }

    const payload = validateAndBuildPayload(data)
    if (!payload) {
      return
    }

    const totalMinutes = Math.max(
      1,
      Math.round(Number(payload.duration ?? 0) * 60),
    )
    createdDraftEntryRef.current = null
    setDraftSplitData({
      payload,
      totalMinutes,
      draftEntry: { id: '', ...payload } as TimeEntry,
      linkedModules: draftLinkedModules,
    })
    setIsDraftSplitOpen(true)
  }

  const handleDraftSplitCancel = () => {
    // If the split failed after the base entry was created, cancel means
    // "nothing saved" — roll the base entry back (best effort).
    const createdEntry = createdDraftEntryRef.current
    if (createdEntry) {
      createdDraftEntryRef.current = null
      deleteTimeEntryMutation.mutate(createdEntry.id)
    }
    setIsDraftSplitOpen(false)
    setDraftSplitData(null)
  }

  // Runs when the user confirms the split: create the base entry first, then
  // the split mutation divides it across the linked items.
  const createDraftEntryForSplit = async (): Promise<TimeEntry | null> => {
    if (!draftSplitData) {
      return null
    }
    // A previous attempt already created the base entry (the split itself
    // failed) — reuse it instead of creating a duplicate.
    if (createdDraftEntryRef.current) {
      return createdDraftEntryRef.current
    }
    const createPayload = targetUserId
      ? { ...draftSplitData.payload, userId: targetUserId }
      : draftSplitData.payload
    const savedEntry = await createTimeEntryMutation.mutateAsync({
      data: createPayload as CreateTimeEntry,
    })
    createdDraftEntryRef.current = savedEntry

    const uniqueTaggedUserIds = Array.from(new Set(pendingTaggedUserIds))
    if (uniqueTaggedUserIds.length > 0) {
      try {
        await replaceTagsForTimeEntryMutation.mutateAsync({
          timeEntryId: savedEntry.id,
          data: { taggedUserIds: uniqueTaggedUserIds },
          suppressErrorToast: true,
        })
      } catch {
        const failedCount = uniqueTaggedUserIds.length
        toast.error(
          `Time entry saved, but couldn't tag ${failedCount} member${failedCount === 1 ? '' : 's'}. Reopen the entry to add them.`,
        )
      }
    }

    return savedEntry
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault()
        // Sync time fields before submitting based on which field is focused
        const currentStartTime = form.getValues('startTime')
        const currentEndTime = form.getValues('endTime')
        const currentHours = form.getValues('hours')

        // Check which field is focused to determine sync direction
        const activeElement = document.activeElement
        const isHoursFieldFocused =
          activeElement?.getAttribute('name') === 'hours'

        if (currentStartTime) {
          if (isHoursFieldFocused && currentHours) {
            // Hours field is focused - sync hours to end time
            const decimalHours = toDecimalStr(currentHours)
            const result = syncHoursToEndTime(currentStartTime, decimalHours)
            if (result.endTime) {
              form.setValue('endTime', result.endTime)
            }
            if (result.hours !== decimalHours) {
              form.setValue('hours', toDisplayStr(result.hours))
            }
          } else if (currentEndTime) {
            // End field or other field is focused - sync end time to hours
            const calculatedHours = syncTimeToHours(
              currentStartTime,
              currentEndTime,
            )
            if (calculatedHours) {
              form.setValue('hours', toDisplayStr(calculatedHours))
            }
          } else if (currentHours) {
            // No end time but hours exist - sync hours to end time
            const decimalHours = toDecimalStr(currentHours)
            const result = syncHoursToEndTime(currentStartTime, decimalHours)
            if (result.endTime) {
              form.setValue('endTime', result.endTime)
            }
            if (result.hours !== decimalHours) {
              form.setValue('hours', toDisplayStr(result.hours))
            }
          }
        }

        form.handleSubmit(
          isMultiLinkCreate ? handleSplitTimes : onSubmit,
          onInvalid,
        )()
      } else {
        // Prevent plain Enter from submitting the form
        e.preventDefault()
      }
    }
  }

  const isFromLinkWorkDropdown = (target: EventTarget | null) =>
    target instanceof HTMLElement &&
    !!target.closest('[data-link-work-dropdown]')
  const isFromMemberTaggingDropdown = (target: EventTarget | null) =>
    target instanceof HTMLElement &&
    !!target.closest('[data-member-tagging-dropdown]')

  return (
    <>
      {/* Hidden (not closed) while the draft split dialog is up so the form
          state survives a Cancel and the user lands back here unchanged. */}
      <Dialog open={open && !isDraftSplitOpen} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn(
            'max-w-[95vw] gap-0 p-0 sm:max-w-[500px]',
            allowTimerEntry && 'lg:max-w-[700px]',
          )}
          onEscapeKeyDown={e => {
            // Prevent dialog from closing if a nested dropdown is open
            if (
              document.querySelector('[data-link-work-dropdown]') ||
              document.querySelector('[data-member-tagging-dropdown]')
            ) {
              e.preventDefault()
            }
          }}
          onInteractOutside={e => {
            const outsideTarget = e.detail.originalEvent.target
            if (
              isFromLinkWorkDropdown(outsideTarget) ||
              isFromMemberTaggingDropdown(outsideTarget)
            ) {
              e.preventDefault()
            }
          }}
          onKeyDown={handleKeyDown}
        >
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit, onInvalid)}>
              {/* Header */}
              <DialogHeader className="border-b bg-muted/30 px-6 pb-4 pt-6">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[hsl(var(--button-primary)/0.2)] via-[hsl(var(--button-primary)/0.1)] to-transparent text-[hsl(var(--button-primary-ink))] shadow-lg">
                    <ClockIcon className="h-6 w-6" weight="duotone" />
                  </div>
                  <div>
                    <DialogTitle className="text-lg font-semibold">
                      {isEntryReadOnly
                        ? 'Time Entry'
                        : entry
                          ? 'Edit Time Entry'
                          : 'New Time Entry'}
                    </DialogTitle>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      {isEntryReadOnly
                        ? 'This entry is locked and cannot be edited'
                        : entry
                          ? 'Update the time entry details'
                          : 'Track your work time'}
                    </p>
                  </div>
                </div>
              </DialogHeader>

              {/* Read-only (submitted/approved) notice */}
              {isEntryReadOnly && (
                <div className="mx-6 mt-4 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                  {resolvedEntry?.status === TimeEntryStatusEnum.APPROVED
                    ? 'This time entry has been approved and can no longer be edited.'
                    : 'This time entry has been submitted and can no longer be edited. Recall it first to make changes.'}
                </div>
              )}

              {/* Period submission warning */}
              {periodWarning && (
                <div className="mx-6 mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  {periodWarning}
                </div>
              )}

              {/* Content */}
              <fieldset disabled={isEntryReadOnly} className="contents">
              <div className="space-y-5 px-6 py-5">
                {/* Date, Time and Hours */}
                <div className="space-y-3">
                  <div
                    className={cn(
                      'grid grid-cols-1 gap-3',
                      allowTimerEntry
                        ? 'sm:grid-cols-2 lg:grid-cols-4'
                        : 'sm:grid-cols-2',
                    )}
                  >
                    <FormField
                      control={form.control}
                      name="date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="flex h-5 items-center gap-2 text-sm font-medium">
                            <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                            Date
                          </FormLabel>
                          <FormControl>
                            <DateInputPicker
                              ref={field.ref}
                              value={field.value}
                              onChange={date => field.onChange(date ?? undefined)}
                              onBlur={field.onBlur}
                              disabledDates={date => {
                                if (date < new Date('1900-01-01')) return true
                                if (!timeSettings.allowFutureEntries) {
                                  const today = new Date()
                                  today.setHours(23, 59, 59, 999)
                                  return date > today
                                }
                                return false
                              }}
                              clearable={false}
                              placeholder="Pick date"
                              triggerClassName="h-9 border-border/40 bg-muted/30"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {allowTimerEntry && (
                      <FormField
                        control={form.control}
                        name="startTime"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex h-5 items-center text-sm font-medium">
                              Start
                            </FormLabel>
                            <FormControl>
                              <SmartTimeInput
                                value={field.value}
                                onChange={value =>
                                  handleTimeChange('startTime', value)
                                }
                                onBlur={handleTimeBlur}
                                placeholder="--:-- AM"
                                className="h-9 border-border/40 bg-muted/30"
                                scrollTarget="past-hour"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                    {allowTimerEntry && (
                      <FormField
                        control={form.control}
                        name="endTime"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="flex h-5 items-center text-sm font-medium">
                              End
                            </FormLabel>
                            <FormControl>
                              <SmartTimeInput
                                value={field.value}
                                onChange={value =>
                                  handleTimeChange('endTime', value)
                                }
                                onBlur={handleTimeBlur}
                                placeholder="--:-- PM"
                                className="h-9 border-border/40 bg-muted/30"
                                referenceTime={startTime}
                                scrollTarget="current"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                    <FormField
                      control={form.control}
                      name="hours"
                      render={({ field }) => {
                        const dayMaxHours = getMaxHours(startTime)
                        const maxHours =
                          timeSettings.maxHoursPerEntry > 0
                            ? Math.min(
                                dayMaxHours,
                                timeSettings.maxHoursPerEntry,
                              )
                            : dayMaxHours
                        return (
                          <FormItem>
                            <FormLabel className="flex h-5 items-center text-sm font-medium">
                              Hours
                            </FormLabel>
                            <FormControl>
                              <Input
                                type="text"
                                inputMode="decimal"
                                placeholder={
                                  getTimeFormat() === 'HH_MM' ? '--:--' : '0.0'
                                }
                                className="h-9 border-border/40 bg-muted/30"
                                {...field}
                                onBlur={handleHoursBlur}
                                value={field.value ?? ''}
                                disabled={!allowManualEntry}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                      {timeEntryHint}
                    </p>
                    {!timezonesMatch && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <GlobeIcon className="h-3.5 w-3.5" />
                        <span>
                          {userTimezoneLabel} ({activeTimezoneAbbr})
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Module Search */}
                <FormField
                  control={form.control}
                  name="moduleId"
                  render={() => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">
                        Link To
                        {timeSettings.requireModuleLink && (
                          <span className="ml-1 text-destructive">*</span>
                        )}
                      </FormLabel>
                      <FormControl>
                        <LinkWorkSelector
                          className="w-full"
                          portalToBody={false}
                          closeOnScroll={false}
                          closeOnSelect
                          allowedModuleTypes={linkWorkAllowedModuleTypes}
                          defaultModule="ALL"
                          value={
                            form.watch('moduleType') && form.watch('moduleId')
                              ? {
                                  type: form.watch('moduleType')!,
                                  id: form.watch('moduleId')!,
                                  name: selectedModuleName,
                                }
                              : null
                          }
                          onSelect={(result: ModuleSearchResult | null) => {
                            if (
                              result &&
                              TIME_ENTRY_MODULE_LINK_TYPE_SET.has(result.type)
                            ) {
                              form.setValue(
                                'moduleType',
                                result.type as TimeEntryModuleLinkType,
                                {
                                  shouldDirty: true,
                                  shouldTouch: true,
                                  shouldValidate: true,
                                },
                              )
                              form.setValue('moduleId', result.id, {
                                shouldDirty: true,
                                shouldTouch: true,
                                shouldValidate: true,
                              })
                              setSelectedModuleName(result.name)
                              setSelectedModuleProjectId(
                                result.type === 'PROJECT'
                                  ? result.id
                                  : result.projectId || null,
                              )
                              setSelectedModuleEntityId(
                                result.type === 'ENTITY'
                                  ? result.id
                                  : result.entityId || null,
                              )
                              form.clearErrors('moduleType')
                              form.clearErrors('moduleId')
                            } else {
                              form.setValue('moduleType', undefined as any, {
                                shouldDirty: true,
                                shouldTouch: true,
                                shouldValidate: true,
                              })
                              form.setValue('moduleId', '', {
                                shouldDirty: true,
                                shouldTouch: true,
                                shouldValidate: true,
                              })
                              setSelectedModuleName(undefined)
                              setSelectedModuleProjectId(null)
                              setSelectedModuleEntityId(null)
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Extra linked items (create mode): time gets split across
                    every linked item via the split dialog on save. */}
                {canMultiLink && (
                  <div className="space-y-2">
                    {extraLinkedModules.map((module, index) => (
                      <div
                        key={
                          module
                            ? `${module.moduleType}-${module.moduleId}`
                            : `extra-link-${index}`
                        }
                        className="flex items-center gap-2"
                      >
                        <LinkWorkSelector
                          className="w-full"
                          portalToBody={false}
                          closeOnScroll={false}
                          closeOnSelect
                          allowedModuleTypes={linkWorkAllowedModuleTypes}
                          defaultModule="ALL"
                          value={
                            module
                              ? {
                                  type: module.moduleType,
                                  id: module.moduleId,
                                  name: module.moduleName ?? undefined,
                                }
                              : null
                          }
                          onSelect={result =>
                            handleExtraLinkSelect(index, result)
                          }
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                          onClick={() => handleRemoveExtraLink(index)}
                          aria-label="Remove linked item"
                        >
                          <XIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    {watchedModuleType && watchedModuleId && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="justify-start gap-2 px-0 text-muted-foreground hover:text-foreground"
                        onClick={() =>
                          setExtraLinkedModules(prev => [...prev, null])
                        }
                      >
                        <PlusIcon className="h-4 w-4" />
                        Link another item
                      </Button>
                    )}
                    {isMultiLinkCreate && (
                      <p className="text-xs text-muted-foreground">
                        Multiple items linked — you&apos;ll choose how to split
                        the time between them next.
                      </p>
                    )}
                  </div>
                )}

                {/* Role Selector */}
                {currentMembership?.id && !targetUserId && (
                  <FormField
                    control={form.control}
                    name="roleId"
                    render={({ field }) => (
                      <FormItem>
                        <RoleSelector
                          memberId={currentMembership.id}
                          projectId={selectedModuleProjectId}
                          entityId={
                            selectedModuleProjectId
                              ? null
                              : selectedModuleEntityId
                          }
                          value={field.value}
                          onChange={field.onChange}
                          hideWhenSingle
                          showWarnings={
                            billingConfiguration.enforceBillingConfiguration
                          }
                          enforcementMode={billingConfiguration.enforcementMode}
                          onAvailableRoleCountChange={
                            setAvailableBillingRoleCount
                          }
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {linkedModules.length > 0 && !usesRealtimeLinkedItems && (
                  <div className="space-y-2 rounded-lg border border-border/40 bg-muted/20 p-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Linked items
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {linkedModules.map(module => (
                        <Badge
                          key={`${module.moduleType}-${module.moduleId}`}
                          variant="secondary"
                        >
                          {module.moduleName || module.moduleType}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description */}
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium">
                        Description
                        {timeSettings.requireDescription && (
                          <span className="ml-1 text-destructive">*</span>
                        )}
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="What did you work on?"
                          className="min-h-[80px] resize-none border-border/40 bg-muted/30"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {resolvedEntry?.id ? (
                  <TimeEntryTagging
                    timeEntryId={resolvedEntry.id}
                    label="Tag Members (Optional)"
                    className="w-full max-w-full"
                    portalToBody={false}
                  />
                ) : (
                  <TimeEntryTagging
                    label="Tag Members (Optional)"
                    className="w-full max-w-full"
                    portalToBody={false}
                    selectedUserIds={pendingTaggedUserIds}
                    onSelectedUserIdsChange={setPendingTaggedUserIds}
                  />
                )}

                {/* Billable Toggle */}
                <FormField
                  control={form.control}
                  name="billable"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border border-border/30 bg-muted/20 p-3">
                      <div>
                        <FormLabel className="text-sm font-medium">
                          Billable
                        </FormLabel>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Mark this time as billable to client
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              {linkedModules.length > 1 &&
                resolvedEntry?.endTime &&
                !usesRealtimeLinkedItems && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-900">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="font-medium">Multiple links detected</p>
                        <p className="text-xs text-amber-800/80">
                          Split this time entry into separate records before
                          submitting.
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => setIsSplitDialogOpen(true)}
                      >
                        Split Time
                      </Button>
                    </div>
                  </div>
                )}
              </fieldset>

              {/* Footer */}
              <DialogFooter className="border-t bg-muted/20 px-6 py-4">
                <div className="flex w-full items-center justify-between">
                  {!isEntryReadOnly && (
                    <span className="hidden items-center gap-1 text-xs text-muted-foreground sm:inline-flex">
                      Press{' '}
                      <kbd className="rounded border bg-muted px-1.5 py-0.5 text-[10px] font-medium">
                        {getModifierKeyLabel()}
                      </kbd>
                      <kbd className="rounded border bg-muted px-1.5 py-0.5 text-[10px] font-medium">
                        Enter
                      </kbd>
                      to save
                    </span>
                  )}
                  <div className="ml-auto flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => onOpenChange(false)}
                      disabled={isSubmitting}
                    >
                      {isEntryReadOnly ? 'Close' : 'Cancel'}
                    </Button>
                    {!isEntryReadOnly && (
                      <Button
                        type="button"
                        disabled={isSubmitting || !!periodWarning}
                        onClick={form.handleSubmit(
                          isMultiLinkCreate ? handleSplitTimes : onSubmit,
                          onInvalid,
                        )}
                      >
                        {isSubmitting ? (
                          <>
                            <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                            Saving...
                          </>
                        ) : entry ? (
                          'Update'
                        ) : isMultiLinkCreate ? (
                          'Split Times'
                        ) : (
                          'Create'
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      {!disableNestedSplitDialog && (
        <SplitTimeEntryDialog
          open={isSplitDialogOpen}
          entry={resolvedEntry}
          onOpenChange={setIsSplitDialogOpen}
          onSplitComplete={() => {
            setIsSplitDialogOpen(false)
            onOpenChange(false)
          }}
        />
      )}
      {/* Draft split: the entry doesn't exist yet — it's created in
          beforeSplit only when the user confirms, so Cancel leaves nothing
          behind and reopens the (still intact) entry form. */}
      {canMultiLink && (
        <SplitTimeEntryDialog
          open={isDraftSplitOpen}
          entry={draftSplitData?.draftEntry ?? null}
          title="Split Time Between Items"
          subtitle="Decide how this entry's time is divided across the linked items."
          linkedModulesOverride={draftSplitData?.linkedModules}
          totalMinutesOverride={draftSplitData?.totalMinutes}
          allowedModuleTypes={linkWorkAllowedModuleTypes}
          allowCancel
          onCancel={handleDraftSplitCancel}
          onOpenChange={setIsDraftSplitOpen}
          beforeSplit={createDraftEntryForSplit}
          onSplitComplete={() => {
            createdDraftEntryRef.current = null
            setIsDraftSplitOpen(false)
            setDraftSplitData(null)
            setExtraLinkedModules([])
            setPendingTaggedUserIds([])
            form.reset()
            onOpenChange(false)
            toast.success('Time entries created')
          }}
        />
      )}
    </>
  )
}
